import React, { useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Button,
  Modal,
  Table,
  ButtonGroup,
  Dropdown,
  Form,
  Col,
  Row,
  InputGroup,
  Spinner,
  Badge,
  Pagination,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { ProductContext } from '../../ProductProvider';
import axios from 'axios';
import Preloader from '../../components/Preloader/Preloader';
import { Helmet } from 'react-helmet-async';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useReactToPrint } from "react-to-print";
import { CSVLink } from "react-csv";
import JournalView from './JournalView';
import './JournalManage.css';

const config = require('../../config.json');

// Dedicated Journal CSV Export Headers
const downloaderJournalInfo = [
  { label: "Journal ID", key: "journalID" },
  { label: "Journal Title", key: "journalTitle" },
  { label: "Subtitle", key: "subtitle" },
  { label: "ISSN", key: "issn" },
  { label: "Frequency", key: "frequency" },
  { label: "Publisher", key: "publisher" },
  { label: "Subject / Category", key: "journalSubject" },
  { label: "Journal Type", key: "journalType" },
  { label: "Subscription Type", key: "subscriptionType" },
  { label: "Subscription Model", key: "subscriptionModal" },
  { label: "Status", key: "journalStatus" },
  { label: "Start Year", key: "startYear" },
  { label: "Language", key: "language" },
  { label: "Price", key: "journalPrice" },
  { label: "Shelf Location", key: "liblocation" },
  { label: "Delivery Mode", key: "delivery" },
  { label: "Website", key: "website" },
  { label: "Contact No", key: "contactNo" },
  { label: "Email", key: "emailID" },
  { label: "Keywords", key: "journalKeywords" }
];

export default function JournalManage() {
  const { enqueueSnackbar } = useSnackbar();
  const {
    validateLogin,
    loggedUser,
    generateDate,
    generateAccessionNo,
    journalData,
    setJournalData
  } = useContext(ProductContext);

  // Data States
  const [loaded, setLoaded] = useState(true);
  const [searchResult, setSearchResult] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchTopic, setSearchTopic] = useState('JournalTitle');
  const [dispMsg, setDispMsg] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Live Filter States
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [frequencyFilter, setFrequencyFilter] = useState('ALL');
  const [formatFilter, setFormatFilter] = useState('ALL');
  const [modelFilter, setModelFilter] = useState('ALL');
  const [locationFilter, setLocationFilter] = useState('ALL');

  // Sorting States
  const [sortConfig, setSortConfig] = useState({ key: 'journalTitle', direction: 'asc' });

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Batch Selection States
  const [selectedJournalIDs, setSelectedJournalIDs] = useState(new Set());

  // Modal States
  const [show, setShow] = useState(false);
  const [boundShow, setBoundShow] = useState(false);
  const [formType, setFormType] = useState('View');
  const [modalInitialTab, setModalInitialTab] = useState('Journal');
  const [viewOnly, setViewOnly] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [imageFile, setImageFile] = useState('');
  const [profileImage, setProfileImage] = useState("images/cat1.png");
  const [newFileName, setNewFileName] = useState('');
  const [isSubmittingBound, setIsSubmittingBound] = useState(false);

  // Bound Journal States
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [issues, setIssues] = useState([]);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [generateAccession, setGenerateAccession] = useState(true);
  const [manualAccession, setManualAccession] = useState('');
  const [customBoundTitle, setCustomBoundTitle] = useState('');
  const [boundLocation, setBoundLocation] = useState('');
  const [yearLabel, setYearLabel] = useState('');
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [issueYearFilter, setIssueYearFilter] = useState('ALL');

  // Scroll to Top
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Refs for Print & CSV
  const csvLinkEl = useRef(null);
  const csvSelectedLinkEl = useRef(null);
  const componentRef = useRef(null);

  // Initial Auth
  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    }
  }, [loggedUser, validateLogin]);

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Search API Call
  const executeSearch = useCallback(async (topic, query) => {
    try {
      setLoaded(false);
      setDispMsg('');
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = "VJCollege";
      const keysearchValue = (query || '').trim().toUpperCase();

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/journal`, {
        params: {
          fetchKey: keysearchValue || 'ALL',
          fetchOption: topic || 'JournalTitle',
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const data = Array.isArray(response.data) ? response.data : [];
      setSearchResult(data);
      setHasSearched(true);
      setDispMsg(`${data.length} Journal${data.length === 1 ? '' : 's'} Found`);
      setSelectedJournalIDs(new Set());
      setCurrentPage(1);
      setLoaded(true);

      if (data.length > 0) {
        enqueueSnackbar(`${data.length} Journal${data.length === 1 ? '' : 's'} Loaded`, { variant: 'success' });
      } else {
        enqueueSnackbar('No journals found for this query', { variant: 'info' });
      }
    } catch (error) {
      console.error("Error searching journals:", error);
      enqueueSnackbar("Error fetching journals: " + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  }, [enqueueSnackbar]);

  const handleSearch = (event) => {
    if (event) event.preventDefault();
    if (!searchValue.trim()) {
      enqueueSnackbar('Please enter search details or click a quick category!', { variant: 'info' });
      return;
    }
    executeSearch(searchTopic, searchValue);
  };

  // Quick Preset Category Search
  const handleQuickCategory = (topic, query) => {
    setSearchTopic(topic);
    setSearchValue(query === 'ALL' ? '' : query);
    executeSearch(topic, query);
  };

  // Auto-fetch on mount with "ALL" to instantly present the journal catalogue
  /*useEffect(() => {
    // executeSearch('JournalTitle', 'ALL');
  }, [executeSearch]);*/

  // Sorting Handler
  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  // Dynamic filter dropdown lists
  const uniqueFrequencies = useMemo(() => {
    const freqs = new Set();
    searchResult.forEach(item => {
      if (item.frequency) freqs.add(item.frequency);
    });
    return Array.from(freqs).sort();
  }, [searchResult]);

  const uniqueLocations = useMemo(() => {
    const locs = new Set();
    searchResult.forEach(item => {
      const loc = item.liblocation || item.location;
      if (loc) locs.add(loc);
    });
    return Array.from(locs).sort();
  }, [searchResult]);

  const uniqueModels = useMemo(() => {
    const models = new Set();
    searchResult.forEach(item => {
      if (item.subscriptionModal) models.add(item.subscriptionModal);
    });
    return Array.from(models).sort();
  }, [searchResult]);

  // KPI Dashboard Metrics
  const kpiStats = useMemo(() => {
    const total = searchResult.length;
    const active = searchResult.filter(j => (j.journalStatus || 'Active').toLowerCase() === 'active').length;
    const printOnly = searchResult.filter(j => (j.subscriptionType || j.journalType || '').toLowerCase().includes('print')).length;
    const onlineOnly = searchResult.filter(j => (j.subscriptionType || j.journalType || '').toLowerCase().includes('online')).length;
    const boundOnly = searchResult.filter(j => (j.journalType || '').trim().toLowerCase() === 'bound journal').length;
    const activePct = total > 0 ? Math.round((active / total) * 100) : 0;

    return { total, active, printOnly, onlineOnly, boundOnly, activePct };
  }, [searchResult]);

  // Filtered & Sorted Journals
  const processedJournals = useMemo(() => {
    let result = [...searchResult];

    // Status Filter
    if (statusFilter !== 'ALL') {
      result = result.filter(j => (j.journalStatus || 'Active') === statusFilter);
    }

    // Frequency Filter
    if (frequencyFilter !== 'ALL') {
      result = result.filter(j => j.frequency === frequencyFilter);
    }

    // Format / Subscription Type Filter
    if (formatFilter !== 'ALL') {
      result = result.filter(j => {
        const typeStr = (j.subscriptionType || j.journalType || '').toLowerCase();
        return typeStr.includes(formatFilter.toLowerCase());
      });
    }

    // Subscription Model Filter
    if (modelFilter !== 'ALL') {
      result = result.filter(j => j.subscriptionModal === modelFilter);
    }

    // Shelf Location Filter
    if (locationFilter !== 'ALL') {
      result = result.filter(j => (j.liblocation || j.location) === locationFilter);
    }

    // Real-time Client Search Box Filter
    if (clientFilter.trim() !== '') {
      const term = clientFilter.toLowerCase();
      result = result.filter(j => {
        const id = (j.journalID || '').toLowerCase();
        const title = (j.journalTitle || '').toLowerCase();
        const sub = (j.subtitle || '').toLowerCase();
        const issn = (j.issn || '').toLowerCase();
        const subject = (j.journalSubject || '').toLowerCase();
        const pub = (j.publisher || '').toLowerCase();
        const freq = (j.frequency || '').toLowerCase();
        const loc = (j.liblocation || j.location || '').toLowerCase();
        const kw = (j.journalKeywords || '').toLowerCase();
        const lang = (j.language || '').toLowerCase();

        return id.includes(term) ||
          title.includes(term) ||
          sub.includes(term) ||
          issn.includes(term) ||
          subject.includes(term) ||
          pub.includes(term) ||
          freq.includes(term) ||
          loc.includes(term) ||
          kw.includes(term) ||
          lang.includes(term);
      });
    }

    // Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let valA = a[sortConfig.key] ?? '';
        let valB = b[sortConfig.key] ?? '';

        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [searchResult, statusFilter, frequencyFilter, formatFilter, modelFilter, locationFilter, clientFilter, sortConfig]);

  // Pagination Slice
  const totalPages = Math.ceil(processedJournals.length / pageSize) || 1;
  const paginatedJournals = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return processedJournals.slice(startIndex, startIndex + pageSize);
  }, [processedJournals, currentPage, pageSize]);

  // Batch Selection Handlers
  const handleToggleSelectAll = () => {
    if (selectedJournalIDs.size === paginatedJournals.length && paginatedJournals.length > 0) {
      setSelectedJournalIDs(new Set());
    } else {
      const newSet = new Set(paginatedJournals.map(j => j.journalID));
      setSelectedJournalIDs(newSet);
    }
  };

  const handleToggleSelectOne = (journalID) => {
    setSelectedJournalIDs(prev => {
      const next = new Set(prev);
      if (next.has(journalID)) {
        next.delete(journalID);
      } else {
        next.add(journalID);
      }
      return next;
    });
  };

  const selectedJournalsData = useMemo(() => {
    return searchResult.filter(j => selectedJournalIDs.has(j.journalID));
  }, [searchResult, selectedJournalIDs]);

  // Modal Handlers
  const handleClose = () => {
    setImageFile('');
    setShow(false);
  };

  const handleBoundClose = () => {
    setBoundShow(false);
    setSelectedIssues([]);
    setIssues([]);
  };

  const handleShow = (event, mode, item = null, tab = 'Journal') => {
    if (event) event.preventDefault();
    setFormType(mode);
    setModalInitialTab(tab);

    if (mode === "View") {
      setViewOnly(true);
      setJournalData(item);
      setModalTitle(`Journal Details: ${item.journalID} - ${item.journalTitle}`);
      const tImageName = `${item.journalID}.jpeg`;
      setProfileImage(`${config.s3.bookPoster}${tImageName}`);
    } else if (mode === "Edit") {
      setViewOnly(false);
      setJournalData(item);
      setModalTitle(`Edit Journal: ${item.journalID} - ${item.journalTitle}`);
      const tImageName = `${item.journalID}.jpeg`;
      setProfileImage(`${config.s3.bookPoster}${tImageName}`);
    } else if (mode === "New") {
      setViewOnly(false);
      setJournalData({
        journalID: '',
        journalType: 'Journal',
        journalTitle: '',
        subtitle: '',
        issn: '',
        publisher: '',
        frequency: 'Monthly',
        journalSubject: '',
        language: 'English',
        startYear: new Date().getFullYear().toString(),
        journalStatus: 'Active',
        description: '',
        subscriptionType: 'Print',
        journalPrice: '0',
        website: '',
        location: 'Periodical Section',
        liblocation: 'Periodical Section',
        country: 'India',
        delivery: 'Post',
        journalKeywords: '',
        contactNo: '',
        emailID: '',
        subscriptionNo: '',
        subscriptionModal: 'Subscription'
      });
      setModalTitle('Catalogue New Journal / Periodical');
    } else if (mode === "Clone") {
      setViewOnly(false);
      const cloned = {
        ...item,
        journalID: '',
        journalTitle: `${item.journalTitle} (Copy)`,
        issn: ''
      };
      setJournalData(cloned);
      setModalTitle(`Clone Journal from [${item.journalID}] ${item.journalTitle}`);
    }
    setShow(true);
  };

  // Direct navigation to Issues tab inside modal
  const handleOpenIssues = (event, item) => {
    handleShow(event, "Edit", item, "Issues");
  };

  // Direct navigation to Articles tab inside modal
  const handleOpenArticles = (event, item) => {
    handleShow(event, "Edit", item, "Articles");
  };

  // Copy ISSN to clipboard
  const handleCopyISSN = (issn, event) => {
    if (event) event.stopPropagation();
    if (issn) {
      navigator.clipboard.writeText(issn);
      enqueueSnackbar(`ISSN ${issn} copied to clipboard!`, { variant: 'info' });
    }
  };

  // Bound Journal Request Handler
  const handleBoundRequest = async (event, mode, item) => {
    if (event) event.preventDefault();
    setSelectedJournal(item);
    setCustomBoundTitle(`${item.journalTitle} - Bound Journal`);
    setBoundLocation(item.liblocation || item.location || 'Bound Journal Section');
    setYearLabel('');
    setIssueYearFilter('ALL');
    setBoundShow(true);
    await fetchJournalIssuesForBinding(item.journalID);
    setModalTitle(`Create Bound Volume: ${item.journalTitle} (${item.journalID})`);
  };

  const fetchJournalIssuesForBinding = async (journalID) => {
    try {
      setLoadingIssues(true);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = "VJCollege";

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/journal/issues`, {
        params: {
          fetchKey: journalID,
          fetchOption: "BoundJournalID",
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const rawIssues = Array.isArray(response.data) ? response.data : [];
      // Sort issues by year desc, vol desc, issue desc
      rawIssues.sort((a, b) => {
        const yearA = Number(a.issueYear || a.year) || 0;
        const yearB = Number(b.issueYear || b.year) || 0;
        if (yearB !== yearA) return yearB - yearA;
        const volA = Number(a.volNo || a.volume) || 0;
        const volB = Number(b.volNo || b.volume) || 0;
        if (volB !== volA) return volB - volA;
        return (Number(b.issueNumber) || 0) - (Number(a.issueNumber) || 0);
      });

      setIssues(rawIssues);
      setSelectedIssues([]);
      setLoadingIssues(false);
    } catch (error) {
      console.error("Error fetching issues for binding:", error);
      enqueueSnackbar("Error fetching issues: " + error.message, { variant: 'warning' });
      setLoadingIssues(false);
    }
  };

  const getIssueKey = (issue) => issue?.id || issue?.volissuesNo || `${issue?.volNo}-${issue?.issueNumber}-${issue?.issueYear || issue?.year}`;

  // Unique years for bound modal
  const uniqueIssueYears = useMemo(() => {
    const yrs = new Set();
    issues.forEach(i => {
      const y = i.issueYear || i.year;
      if (y) yrs.add(String(y));
    });
    return Array.from(yrs).sort((a, b) => Number(b) - Number(a));
  }, [issues]);

  // Filtered issues inside bound modal
  const displayedIssues = useMemo(() => {
    if (issueYearFilter === 'ALL') return issues;
    return issues.filter(i => String(i.issueYear || i.year) === issueYearFilter);
  }, [issues, issueYearFilter]);

  // Issue Selection for Bound Journal
  const handleIssueSelection = (issue) => {
    const issueKey = getIssueKey(issue);
    setSelectedIssues((prev) => {
      const exists = prev.some(i => getIssueKey(i) === issueKey);
      const updated = exists
        ? prev.filter(i => getIssueKey(i) !== issueKey)
        : [...prev, issue];

      // Update Year Range
      if (updated.length === 0) {
        setYearLabel('');
        setCustomBoundTitle(`${selectedJournal?.journalTitle} - Bound Journal`);
      } else {
        const years = updated
          .map(i => Number(i.issueYear || i.year))
          .filter(y => !isNaN(y) && y > 0);

        if (years.length > 0) {
          const minYear = Math.min(...years);
          const maxYear = Math.max(...years);
          const label = minYear === maxYear ? `${minYear}` : `${minYear} - ${maxYear}`;
          setYearLabel(label);
          setCustomBoundTitle(`${selectedJournal?.journalTitle} - Bound Journal - ${label}`);
        }
      }

      return updated;
    });
  };

  // Select All Issues
  const handleSelectAllIssues = () => {
    if (selectedIssues.length === displayedIssues.length && displayedIssues.length > 0) {
      setSelectedIssues([]);
      setYearLabel('');
      setCustomBoundTitle(`${selectedJournal?.journalTitle} - Bound Journal`);
    } else {
      setSelectedIssues([...displayedIssues]);
      const years = displayedIssues
        .map(i => Number(i.issueYear || i.year))
        .filter(y => !isNaN(y) && y > 0);

      if (years.length > 0) {
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years);
        const label = minYear === maxYear ? `${minYear}` : `${minYear} - ${maxYear}`;
        setYearLabel(label);
        setCustomBoundTitle(`${selectedJournal?.journalTitle} - Bound Journal - ${label}`);
      }
    }
  };

  // Select only Received issues
  const handleSelectReceivedOnly = () => {
    const received = displayedIssues.filter(i => i.issueStatus === 'Received');
    setSelectedIssues(received);
    const years = received
      .map(i => Number(i.issueYear || i.year))
      .filter(y => !isNaN(y) && y > 0);

    if (years.length > 0) {
      const minYear = Math.min(...years);
      const maxYear = Math.max(...years);
      const label = minYear === maxYear ? `${minYear}` : `${minYear} - ${maxYear}`;
      setYearLabel(label);
      setCustomBoundTitle(`${selectedJournal?.journalTitle} - Bound Journal - ${label}`);
    }
  };

  // Bound Journal Submit
  const handleBoundSubmit = async (event) => {
    if (event) event.preventDefault();
    if (selectedIssues.length === 0) {
      enqueueSnackbar("Please select at least one issue to bind.", { variant: 'warning' });
      return;
    }

    try {
      setIsSubmittingBound(true);
      let accessionNumber = '';

      if (generateAccession) {
        accessionNumber = await generateAccessionNo();
      } else {
        if (!manualAccession.trim()) {
          enqueueSnackbar("Please enter a valid Accession Number", { variant: 'warning' });
          setIsSubmittingBound(false);
          return;
        }
        accessionNumber = manualAccession.trim();
      }

      const params = {
        clientID: "VJCollege",
        accessionNo: accessionNumber,
        registeredOn: generateDate(),
        autoGenerate: generateAccession,
        bookAuthor: "Bound Journal",
        bookCategory: selectedJournal.journalSubject || "Journal",
        bookClassificationNo: "NA",
        bookEdition: "NA",
        bookPages: "0",
        bookPrice: selectedJournal.journalPrice || "0",
        bookPublishYear: selectedJournal.startYear || new Date().getFullYear().toString(),
        bookPublisherName: selectedJournal.publisher || "NA",
        bookScope: "Reference",
        bookStatus: "Active",
        bookLocation: boundLocation || selectedJournal.liblocation || selectedJournal.location || "Bound Journal Section",
        bookTitle: customBoundTitle || `${selectedJournal?.journalTitle} - Bound Journal - ${yearLabel}`,
        updatedBy: loggedUser.username || "Librarian",
        updatedOn: generateDate(),
        authorMark: "NA",
        bookAbstract: "NA",
        bookKeywords: selectedJournal.journalKeywords || "",
        bookCopies: "1",
        bookPublisherLocation: selectedJournal.subscriptionModal || "",
        bookSubTitle: selectedJournal.subtitle || "",
        isbn: selectedJournal.issn || "",
        bookDimension: "NA",
        journalID: selectedJournal.journalID,
        issues: selectedIssues
      };

      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const res = await axios.post(`${config.api.invokeURL}/vjlibrary/journal/boundjournal`, params, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      // Update issues to mark as Bounded Journal
      const patchParams = {
        clientID: "VJCollege",
        issueTag: "Bounded Journal",
        issueAccessionNo: res.data || accessionNumber,
        updatedBy: loggedUser.username || "Librarian",
        updatedOn: generateDate(),
        selectedIssues: selectedIssues
      };

      await axios.patch(`${config.api.invokeURL}/vjlibrary/journal/boundjournal`, patchParams, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      enqueueSnackbar(`Bound Journal volume created successfully with Accession Number: ${res.data || accessionNumber}`, { variant: 'success' });
      handleBoundClose();
    } catch (error) {
      console.error("Error creating bound journal:", error);
      enqueueSnackbar("Error creating bound journal: " + (error.response?.data?.message || error.message), { variant: 'error' });
    } finally {
      setIsSubmittingBound(false);
    }
  };

  // CSV & Print triggers
  const handleDownloadAll = (event) => {
    if (event) event.preventDefault();
    if (csvLinkEl.current?.link) {
      csvLinkEl.current.link.click();
    }
  };

  const handleDownloadSelected = (event) => {
    if (event) event.preventDefault();
    if (csvSelectedLinkEl.current?.link) {
      csvSelectedLinkEl.current.link.click();
    }
  };

  const printFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Vidyajyoti College - Journal Catalogue (${new Date().toLocaleDateString()})`,
  });

  const resetAllFilters = () => {
    setClientFilter('');
    setStatusFilter('ALL');
    setFrequencyFilter('ALL');
    setFormatFilter('ALL');
    setModelFilter('ALL');
    setLocationFilter('ALL');
    setCurrentPage(1);
  };

  const isFilterActive = clientFilter !== '' ||
    statusFilter !== 'ALL' ||
    frequencyFilter !== 'ALL' ||
    formatFilter !== 'ALL' ||
    modelFilter !== 'ALL' ||
    locationFilter !== 'ALL';

  return (
    <>
      <Helmet>
        <title>Journal Management - Vidyajyoti College of Theology Library</title>
        <meta name="description" content="Manage periodicals, journal subscriptions, issues tracking, article indexing, and bound volumes." />
      </Helmet>

      {!loaded && <Preloader show={true} />}

      <div className="journal-manage-container">
        {/* Top Header Card */}
        <div className="journal-manage-header d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="journal-manage-title d-flex align-items-center gap-2">
              <span className="journal-header-icon">
                <i className="bi bi-journal-album"></i>
              </span>
              <span>Periodicals & Journal Catalogue</span>
            </div>
            <div className="journal-manage-subtitle">
              Manage subscriptions, receive & track issues, catalogue research articles, and bind completed volumes.
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button
              className="btn-journal-primary"
              onClick={(e) => handleShow(e, "New")}
            >
              <i className="bi bi-plus-circle me-1"></i> Add New Journal
            </Button>

            <ButtonGroup className="shadow-sm">
              <OverlayTrigger placement="bottom" overlay={<Tooltip>Print Filtered Journal List</Tooltip>}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={printFn}
                  className="py-2 px-3 bg-white"
                >
                  <i className="bi bi-printer me-1"></i> Print
                </Button>
              </OverlayTrigger>

              <OverlayTrigger placement="bottom" overlay={<Tooltip>Export Filtered List to CSV</Tooltip>}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDownloadAll}
                  className="py-2 px-3 bg-white"
                >
                  <i className="bi bi-file-earmark-spreadsheet me-1"></i> Export CSV
                </Button>
              </OverlayTrigger>
            </ButtonGroup>

            <CSVLink
              headers={downloaderJournalInfo}
              filename={`VJ_Journals_Catalogue_${new Date().toISOString().split('T')[0]}.csv`}
              data={processedJournals}
              ref={csvLinkEl}
              className="d-none"
            />

            <CSVLink
              headers={downloaderJournalInfo}
              filename={`VJ_Journals_Selected_${new Date().toISOString().split('T')[0]}.csv`}
              data={selectedJournalsData}
              ref={csvSelectedLinkEl}
              className="d-none"
            />
          </div>
        </div>

        {/* KPI Metric Summary Cards */}
        <div className="journal-kpi-grid">
          <div
            className={`journal-kpi-card kpi-border-total ${statusFilter === 'ALL' && !isFilterActive ? 'kpi-card-active' : ''}`}
            onClick={resetAllFilters}
            title="Click to view all journals"
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="journal-kpi-label">Total Periodicals</div>
              <div className="kpi-icon-pill bg-blue-soft"><i className="bi bi-collection text-primary"></i></div>
            </div>
            <div className="journal-kpi-value text-primary">{kpiStats.total}</div>
            <div className="journal-kpi-subtext">Catalogued in library</div>
          </div>

          <div
            className={`journal-kpi-card kpi-border-active ${statusFilter === 'Active' ? 'kpi-card-active' : ''}`}
            onClick={() => { setStatusFilter(statusFilter === 'Active' ? 'ALL' : 'Active'); setCurrentPage(1); }}
            title="Click to toggle Active subscriptions filter"
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="journal-kpi-label">Active Subscriptions</div>
              <div className="kpi-icon-pill bg-green-soft"><i className="bi bi-check-circle text-success"></i></div>
            </div>
            <div className="journal-kpi-value text-success">{kpiStats.active}</div>
            <div className="journal-kpi-subtext">{kpiStats.activePct}% active receiving</div>
          </div>

          <div
            className={`journal-kpi-card kpi-border-frequency ${formatFilter === 'Print' ? 'kpi-card-active' : ''}`}
            onClick={() => { setFormatFilter(formatFilter === 'Print' ? 'ALL' : 'Print'); setCurrentPage(1); }}
            title="Click to toggle Print filter"
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="journal-kpi-label">Print Subscriptions</div>
              <div className="kpi-icon-pill bg-amber-soft"><i className="bi bi-book text-warning"></i></div>
            </div>
            <div className="journal-kpi-value text-warning">{kpiStats.printOnly}</div>
            <div className="journal-kpi-subtext">Physical shelf copies</div>
          </div>

          <div
            className={`journal-kpi-card kpi-border-type ${formatFilter === 'Online' ? 'kpi-card-active' : ''}`}
            onClick={() => { setFormatFilter(formatFilter === 'Online' ? 'ALL' : 'Online'); setCurrentPage(1); }}
            title="Click to toggle Online filter"
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="journal-kpi-label">Online / Electronic</div>
              <div className="kpi-icon-pill bg-purple-soft"><i className="bi bi-globe text-purple"></i></div>
            </div>
            <div className="journal-kpi-value text-purple">{kpiStats.onlineOnly}</div>
            <div className="journal-kpi-subtext">Digital access journals</div>
          </div>
        </div>

        {/* Search & Discovery Panel */}
        <div className="journal-search-panel">
          {/* Quick Category Chips */}
          <div className="journal-chips-bar d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className="small fw-bold text-muted me-1">
              <i className="bi bi-lightning-charge-fill text-warning me-1"></i>Quick Browse:
            </span>
            <button
              type="button"
              className={`journal-quick-chip ${searchValue === '' && searchTopic === 'JournalTitle' ? 'chip-selected' : ''}`}
              onClick={() => handleQuickCategory('JournalSubject', 'JESUITS')}
            >
              <i className="bi bi-journals me-1"></i> Jesuits
            </button>
            <button
              type="button"
              className="journal-quick-chip"
              onClick={() => handleQuickCategory('JournalSubject', 'THEOLOGY')}
            >
              <i className="bi bi-book-half me-1"></i> Theology
            </button>
            <button
              type="button"
              className="journal-quick-chip"
              onClick={() => handleQuickCategory('JournalSubject', 'PASTORALIA')}
            >
              <i className="bi bi-mortarboard me-1"></i> Pastoralia
            </button>
            <button
              type="button"
              className="journal-quick-chip"
              onClick={() => handleQuickCategory('JournalSubject', 'SPIRITUALITY')}
            >
              <i className="bi bi-bookmark-star me-1"></i> Spirituality
            </button>
            <button
              type="button"
              className="journal-quick-chip"
              onClick={() => handleQuickCategory('JournalSubject', 'SCRIPTURE')}
            >
              <i className="bi bi-sun me-1"></i> Scripture
            </button>
            <button
              type="button"
              className="journal-quick-chip"
              onClick={() => handleQuickCategory('JournalSubject', 'CANON LAW')}
            >
              <i className="bi bi-hourglass-split me-1"></i> Canon Law
            </button>
            {kpiStats.boundOnly > 0 && (
              <button
                type="button"
                className={`journal-quick-chip ${formatFilter === 'Bound Journal' ? 'chip-selected' : ''}`}
                onClick={() => { setFormatFilter(formatFilter === 'Bound Journal' ? 'ALL' : 'Bound Journal'); setCurrentPage(1); }}
                title="Filter by Bound Journals"
              >
                <i className="bi bi-journal-bookmark-fill me-1 text-primary"></i> Bound Journals ({kpiStats.boundOnly})
              </button>
            )}
          </div>

          {/* Search Form */}
          <Form onSubmit={handleSearch}>
            <Row className="g-3 align-items-center">
              <Col lg={3} md={4} sm={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted mb-1">Search Field</Form.Label>
                  <Form.Select
                    className="journal-select"
                    value={searchTopic}
                    onChange={(e) => setSearchTopic(e.target.value)}
                  >
                    <option value="JournalTitle">Journal Title</option>
                    <option value="JournalID">Journal ID</option>
                    <option value="ISSN">ISSN Number</option>
                    <option value="JournalSubject">Subject / Category</option>
                    <option value="Publisher">Publisher</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col lg={6} md={5} sm={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted mb-1">Search Keywords</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      className="journal-search-input border-start-0"
                      placeholder={`Enter ${searchTopic.replace('Journal', 'Journal ')} (or leave blank and search ALL)...`}
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                    />
                    {searchValue && (
                      <Button variant="outline-secondary" onClick={() => setSearchValue('')}>
                        <i className="bi bi-x-lg"></i>
                      </Button>
                    )}
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col lg={3} md={3} sm={12} className="d-flex align-items-end">
                <Button type="submit" className="btn-journal-search w-100 py-2">
                  <i className="bi bi-arrow-repeat me-1"></i> Search Database
                </Button>
              </Col>
            </Row>
          </Form>

          {/* Real-time In-Page Filters */}
          {searchResult.length > 0 && (
            <div className="journal-filters-bar mt-3 pt-3 border-top">
              <Row className="g-2 align-items-center">
                {/* Live Keyword Filter */}
                <Col xl={3} lg={4} md={6} sm={12}>
                  <InputGroup size="sm">
                    <InputGroup.Text className="bg-white">
                      <i className="bi bi-funnel text-muted"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Filter title, ID, ISSN, shelf..."
                      value={clientFilter}
                      onChange={(e) => {
                        setClientFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                    {clientFilter && (
                      <Button variant="outline-secondary" onClick={() => setClientFilter('')}>
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </InputGroup>
                </Col>

                {/* Status Pills */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <div className="d-flex flex-wrap align-items-center gap-1">
                    <span className="small text-muted me-1 fw-semibold">Status:</span>
                    <span
                      className={`status-pill ${statusFilter === 'ALL' ? 'active' : ''}`}
                      onClick={() => { setStatusFilter('ALL'); setCurrentPage(1); }}
                    >
                      All ({searchResult.length})
                    </span>
                    <span
                      className={`status-pill status-pill-active ${statusFilter === 'Active' ? 'active' : ''}`}
                      onClick={() => { setStatusFilter('Active'); setCurrentPage(1); }}
                    >
                      Active ({kpiStats.active})
                    </span>
                    <span
                      className={`status-pill status-pill-inactive ${statusFilter === 'Inactive' ? 'active' : ''}`}
                      onClick={() => { setStatusFilter('Inactive'); setCurrentPage(1); }}
                    >
                      Inactive
                    </span>
                    <span
                      className={`status-pill status-pill-discontinued ${statusFilter === 'Discontinued' ? 'active' : ''}`}
                      onClick={() => { setStatusFilter('Discontinued'); setCurrentPage(1); }}
                    >
                      Discontinued
                    </span>
                  </div>
                </Col>

                {/* Format Filter */}
                <Col xl={2} lg={2} md={4} sm={6}>
                  <Form.Select
                    size="sm"
                    value={formatFilter}
                    onChange={(e) => { setFormatFilter(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="ALL">All Formats</option>
                    <option value="Print">Print Only</option>
                    <option value="Online">Online / Digital</option>
                    <option value="Bound Journal">Bound Journal</option>
                  </Form.Select>
                </Col>

                {/* Frequency Filter */}
                {uniqueFrequencies.length > 0 && (
                  <Col xl={2} lg={2} md={4} sm={6}>
                    <Form.Select
                      size="sm"
                      value={frequencyFilter}
                      onChange={(e) => { setFrequencyFilter(e.target.value); setCurrentPage(1); }}
                    >
                      <option value="ALL">All Frequencies</option>
                      {uniqueFrequencies.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </Form.Select>
                  </Col>
                )}

                {/* Reset Filters */}
                {isFilterActive && (
                  <Col xl={1} lg={12} md={12} className="text-end">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0 text-decoration-none fw-bold small"
                      onClick={resetAllFilters}
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>Reset
                    </Button>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </div>

        {/* Floating Batch Action Toolbar when items are selected */}
        {selectedJournalIDs.size > 0 && (
          <div className="journal-batch-toolbar d-flex flex-wrap justify-content-between align-items-center gap-2 shadow-lg mb-3">
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary fs-6 px-3 py-2">
                <i className="bi bi-check2-circle me-1"></i> {selectedJournalIDs.size} Selected
              </span>
              <span className="small text-muted">
                Action on selected periodicals:
              </span>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                className="bg-white"
                onClick={handleDownloadSelected}
              >
                <i className="bi bi-file-earmark-spreadsheet me-1"></i> Export Selected ({selectedJournalIDs.size})
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="bg-white"
                onClick={() => setSelectedJournalIDs(new Set())}
              >
                <i className="bi bi-x-circle me-1"></i> Clear Selection
              </Button>
            </div>
          </div>
        )}

        {/* Message / Results Meta Indicator */}
        {dispMsg && (
          <div className="d-flex justify-content-between align-items-center mb-2 px-1">
            <span className="text-muted small fw-bold">
              <i className="bi bi-info-circle me-1 text-primary"></i> {dispMsg}
              {isFilterActive && (
                <span className="text-primary ms-2">
                  (Showing {processedJournals.length} filtered results)
                </span>
              )}
            </span>
            {processedJournals.length > 0 && (
              <span className="text-muted small">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, processedJournals.length)} of {processedJournals.length} periodicals
              </span>
            )}
          </div>
        )}

        {/* Main Journals Table */}
        {searchResult.length === 0 ? (
          <div className="journal-table-card journal-empty-state">
            <div className="journal-empty-icon">
              <i className="bi bi-journal-text"></i>
            </div>
            <div className="journal-empty-title">
              {hasSearched ? "No Periodicals Found" : "Loading Periodicals..."}
            </div>
            <div className="journal-empty-text">
              {hasSearched
                ? "No periodicals match your search query. Try searching with different keywords or click below to catalogue a new journal."
                : "Looking up the library's periodical collection..."}
            </div>
            <div className="d-flex justify-content-center gap-2">
              <Button className="btn-journal-primary" onClick={(e) => handleShow(e, "New")}>
                <i className="bi bi-plus-lg me-1"></i> Add New Journal
              </Button>
              <Button variant="outline-primary" onClick={() => executeSearch('JournalTitle', 'ALL')}>
                <i className="bi bi-arrow-repeat me-1"></i> Load All Periodicals
              </Button>
            </div>
          </div>
        ) : processedJournals.length === 0 ? (
          <div className="journal-table-card journal-empty-state">
            <div className="journal-empty-icon text-muted">
              <i className="bi bi-funnel"></i>
            </div>
            <div className="journal-empty-title">No Matching Periodicals</div>
            <div className="journal-empty-text">
              No journals match your active filters. Try adjusting your search term or reset all filters.
            </div>
            <Button
              variant="outline-primary"
              onClick={resetAllFilters}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i> Reset All Filters
            </Button>
          </div>
        ) : (
          <div className="journal-table-card">
            <div className="table-responsive">
              <Table hover className="journal-main-table align-middle" ref={componentRef}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }} className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={paginatedJournals.length > 0 && paginatedJournals.every(j => selectedJournalIDs.has(j.journalID))}
                        onChange={handleToggleSelectAll}
                        title="Select all on current page"
                      />
                    </th>
                    <th style={{ width: '50px' }}>#</th>
                    <th className="sortable-th" onClick={() => handleSort('journalID')} style={{ minWidth: '120px' }}>
                      Journal ID
                      {sortConfig.key === 'journalID' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th" onClick={() => handleSort('journalTitle')} style={{ minWidth: '280px' }}>
                      Journal Title & Details
                      {sortConfig.key === 'journalTitle' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th" onClick={() => handleSort('frequency')} style={{ minWidth: '110px' }}>
                      Frequency
                      {sortConfig.key === 'frequency' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th" onClick={() => handleSort('journalSubject')} style={{ minWidth: '130px' }}>
                      Subject / Field
                      {sortConfig.key === 'journalSubject' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th" onClick={() => handleSort('subscriptionType')} style={{ minWidth: '120px' }}>
                      Format / Type
                      {sortConfig.key === 'subscriptionType' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th" onClick={() => handleSort('liblocation')} style={{ minWidth: '120px' }}>
                      Shelf Location
                      {sortConfig.key === 'liblocation' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th text-center" onClick={() => handleSort('journalStatus')} style={{ minWidth: '100px' }}>
                      Status
                      {sortConfig.key === 'journalStatus' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="text-end" style={{ minWidth: '160px' }}>Quick Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedJournals.map((item, index) => {
                    const rowNumber = ((currentPage - 1) * pageSize) + index + 1;
                    const status = item.journalStatus || 'Active';
                    const statusClass =
                      status === 'Active'
                        ? 'badge-journal-active'
                        : status === 'Discontinued'
                          ? 'badge-journal-discontinued'
                          : 'badge-journal-inactive';

                    const isSelected = selectedJournalIDs.has(item.journalID);
                    const shelfLoc = item.liblocation || item.location;
                    const isBoundJournal = (item.journalType || '').trim().toLowerCase() === 'bound journal';

                    return (
                      <tr key={item.journalID || index} className={isSelected ? 'table-selected-row' : ''}>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelectOne(item.journalID)}
                          />
                        </td>
                        <td className="text-muted small">{rowNumber}</td>
                        <td>
                          <div className="d-flex flex-column align-items-start gap-1">
                            <span className="journal-id-badge">{item.journalID}</span>
                            {item.issn && (
                              <OverlayTrigger placement="right" overlay={<Tooltip>Click to copy ISSN</Tooltip>}>
                                <span
                                  className="issn-tag"
                                  onClick={(e) => handleCopyISSN(item.issn, e)}
                                >
                                  <i className="bi bi-upc-scan me-1"></i> {item.issn}
                                </span>
                              </OverlayTrigger>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="journal-title-cell">
                            <div className="d-flex flex-wrap align-items-center gap-1">
                              <span className="fw-bold text-dark journal-row-title" onClick={(e) => handleShow(e, "View", item)}>
                                {item.journalTitle}
                              </span>
                              {isBoundJournal && (
                                <span className="badge-bound-journal">
                                  <i className="bi bi-journal-bookmark-fill"></i> Bound Journal
                                </span>
                              )}
                            </div>
                            {item.subtitle && (
                              <div className="small text-muted text-truncate" style={{ maxWidth: 320 }}>
                                {item.subtitle}
                              </div>
                            )}
                            <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                              {item.publisher && (
                                <span className="small text-secondary">
                                  <i className="bi bi-building me-1"></i> {item.publisher}
                                </span>
                              )}
                              {item.startYear && (
                                <span className="small text-muted">
                                  (Est. {item.startYear})
                                </span>
                              )}
                              {item.website && (
                                <a
                                  href={item.website.startsWith('http') ? item.website : `https://${item.website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="journal-web-link small text-primary"
                                  title="Open Official Website"
                                >
                                  <i className="bi bi-box-arrow-up-right"></i> Website
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="frequency-pill">
                            <i className="bi bi-calendar3 me-1"></i>
                            {item.frequency || 'Monthly'}
                          </span>
                        </td>
                        <td>
                          <span className="subject-tag">
                            {item.journalSubject || 'General'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start gap-1">
                            {isBoundJournal ? (
                              <span className="badge-bound-journal">
                                <i className="bi bi-journal-bookmark-fill"></i> Bound Journal
                              </span>
                            ) : (item.subscriptionType || item.journalType || '').toLowerCase().includes('online') ? (
                              <span className="format-badge">
                                <i className="bi bi-globe me-1 text-purple"></i> Online
                              </span>
                            ) : (
                              <span className="format-badge">
                                <i className="bi bi-book me-1 text-primary"></i> Print
                              </span>
                            )}
                            {item.subscriptionModal && (
                              <span className="small text-muted">
                                {item.subscriptionModal}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {shelfLoc ? (
                            <span className="shelf-location-tag">
                              <i className="bi bi-geo-alt-fill me-1 text-danger"></i> {shelfLoc}
                            </span>
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={statusClass}>
                            {status === 'Active' && <span className="status-indicator-dot"></span>}
                            {status}
                          </span>
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end align-items-center gap-1">
                            {/* Fast Direct Action: Issues */}
                            <OverlayTrigger placement="top" overlay={<Tooltip>Manage Issues Tracker</Tooltip>}>
                              <Button
                                variant="light"
                                size="sm"
                                className="btn-table-action action-issues"
                                onClick={(e) => handleOpenIssues(e, item)}
                              >
                                <i className="bi bi-calendar-check"></i>
                              </Button>
                            </OverlayTrigger>

                            {/* Fast Direct Action: Articles */}
                            <OverlayTrigger placement="top" overlay={<Tooltip>Catalogue Articles Index</Tooltip>}>
                              <Button
                                variant="light"
                                size="sm"
                                className="btn-table-action action-articles"
                                onClick={(e) => handleOpenArticles(e, item)}
                              >
                                <i className="bi bi-file-earmark-text"></i>
                              </Button>
                            </OverlayTrigger>

                            {/* Fast Direct Action: Edit */}
                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit Journal Info</Tooltip>}>
                              <Button
                                variant="light"
                                size="sm"
                                className="btn-table-action action-edit"
                                onClick={(e) => handleShow(e, "Edit", item)}
                              >
                                <i className="bi bi-pencil-square"></i>
                              </Button>
                            </OverlayTrigger>

                            {/* Dropdown for More Actions */}
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="light"
                                size="sm"
                                className="btn-table-action border-0 bg-transparent text-secondary p-1"
                                id={`dropdown-journal-${index}`}
                              >
                                <i className="bi bi-three-dots-vertical"></i>
                              </Dropdown.Toggle>

                              <Dropdown.Menu className="journal-action-menu shadow-lg">
                                <Dropdown.Item onClick={(e) => handleShow(e, "View", item)}>
                                  <i className="bi bi-eye text-primary"></i> Full Journal Overview
                                </Dropdown.Item>
                                <Dropdown.Item onClick={(e) => handleShow(e, "Edit", item)}>
                                  <i className="bi bi-pencil-square text-warning"></i> Edit Journal Metadata
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={(e) => handleOpenIssues(e, item)}>
                                  <i className="bi bi-calendar-check text-success"></i> Issue Receiving & Logging
                                </Dropdown.Item>
                                <Dropdown.Item onClick={(e) => handleOpenArticles(e, item)}>
                                  <i className="bi bi-file-earmark-text text-info"></i> Article Indexing
                                </Dropdown.Item>
                                <Dropdown.Item onClick={(e) => handleBoundRequest(e, "Bound", item)}>
                                  <i className="bi bi-journal-bookmark text-indigo"></i> Create Bound Volume
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={(e) => handleShow(e, "Clone", item)}>
                                  <i className="bi bi-copy text-secondary"></i> Clone as New Template
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-bar">
                <div className="d-flex align-items-center gap-2">
                  <span className="small text-muted">Rows per page:</span>
                  <Form.Select
                    size="sm"
                    style={{ width: '80px' }}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </div>

                <Pagination size="sm" className="mb-0">
                  <Pagination.First
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  />

                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum = i + 1;
                    if (totalPages > 5 && currentPage > 3) {
                      pageNum = currentPage - 3 + i;
                      if (pageNum > totalPages) pageNum = totalPages - (4 - i);
                    }
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}

                  <Pagination.Next
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ================= MODAL: JOURNAL VIEW / EDIT / ISSUES / ARTICLES ================= */}
      <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static" className="journal-custom-modal">
        <Modal.Header closeButton className="modal-custom-header">
          <Modal.Title className="h5 fw-bold text-white mb-0">
            <i className="bi bi-journal-text me-2"></i>
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-white">
          <JournalView
            viewOnly={viewOnly}
            handleClose={handleClose}
            formType={formType}
            initialTab={modalInitialTab}
            dispMsg={dispMsg}
            setNewFileName={setNewFileName}
            setImageFile={setImageFile}
            profileImage={profileImage}
          />
        </Modal.Body>
      </Modal>

      {/* ================= MODAL: BOUND JOURNAL CREATION WIZARD ================= */}
      <Modal show={boundShow} onHide={handleBoundClose} size="xl" centered backdrop="static" className="bound-custom-modal">
        <Modal.Header closeButton className="modal-custom-header">
          <Modal.Title className="h5 fw-bold text-white mb-0">
            <i className="bi bi-journal-bookmark me-2"></i>
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {/* Wizard Header Info Banner */}
          <div className="bound-wizard-header">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div>
                <div className="bound-wizard-title">
                  <i className="bi bi-journal-check me-2"></i> Bound Volume Creation Wizard
                </div>
                <div className="small text-muted mt-1">
                  Bind multiple loose periodical issues into a permanent catalogued reference book in the library collection.
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-primary px-3 py-2">
                  Journal ID: {selectedJournal?.journalID}
                </span>
                {selectedJournal?.issn && (
                  <span className="badge bg-light text-dark border px-3 py-2">
                    ISSN: {selectedJournal.issn}
                  </span>
                )}
              </div>
            </div>
          </div>

          {loadingIssues ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="small text-muted mt-2">Loading available issues for binding...</div>
            </div>
          ) : issues.length === 0 ? (
            <div className="text-center py-5 border rounded bg-light">
              <i className="bi bi-exclamation-circle text-warning fs-1 mb-3 d-block"></i>
              <h5>No Unbound Issues Found</h5>
              <p className="text-muted small mb-3 max-w-400 mx-auto">
                There are no unbound issues recorded for <strong>{selectedJournal?.journalTitle}</strong>.
                Receive loose issues in the Issues Tracker before creating a bound volume.
              </p>
              <Button variant="secondary" onClick={handleBoundClose}>Close</Button>
            </div>
          ) : (
            <div>
              {/* Bound Journal Metadata Form */}
              <Form className="p-3 mb-3 bg-light rounded-3 border">
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="fw-bold small text-muted">Bound Volume Title *</Form.Label>
                      <Form.Control
                        type="text"
                        value={customBoundTitle}
                        onChange={(e) => setCustomBoundTitle(e.target.value)}
                        placeholder="e.g. Journal Title - Bound Journal - 2023 - 2024"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold small text-muted">Shelf Location</Form.Label>
                      <Form.Control
                        type="text"
                        value={boundLocation}
                        onChange={(e) => setBoundLocation(e.target.value)}
                        placeholder="e.g. Bound Section Rack 4"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold small text-muted">Accession Number Mode</Form.Label>
                      <div className="mt-2">
                        <Form.Check
                          type="checkbox"
                          id="autoAccessionCheck"
                          label="Auto-generate Accession Number"
                          checked={generateAccession}
                          onChange={(e) => setGenerateAccession(e.target.checked)}
                          className="fw-semibold text-primary"
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  {!generateAccession && (
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold small text-muted">Manual Accession Number *</Form.Label>
                        <Form.Control
                          type="text"
                          value={manualAccession}
                          onChange={(e) => setManualAccession(e.target.value)}
                          placeholder="Enter Accession Number"
                          required
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </Form>

              {/* Action Toolbar for Issues Selection */}
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleSelectAllIssues}
                  >
                    {selectedIssues.length === displayedIssues.length && displayedIssues.length > 0
                      ? 'Deselect All'
                      : 'Select All Issues'}
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={handleSelectReceivedOnly}
                  >
                    <i className="bi bi-check2-all me-1"></i> Select Received Only
                  </Button>
                  <span className="badge bg-primary px-3 py-2">
                    Selected: {selectedIssues.length} of {issues.length} issues
                  </span>
                  {yearLabel && (
                    <span className="badge bg-success px-3 py-2">
                      Year Span: {yearLabel}
                    </span>
                  )}
                </div>

                {/* Filter by Year inside modal */}
                {uniqueIssueYears.length > 1 && (
                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted fw-semibold">Year:</span>
                    <Form.Select
                      size="sm"
                      style={{ width: '120px' }}
                      value={issueYearFilter}
                      onChange={(e) => setIssueYearFilter(e.target.value)}
                    >
                      <option value="ALL">All Years</option>
                      {uniqueIssueYears.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </Form.Select>
                  </div>
                )}
              </div>

              {/* Issues Selection Table */}
              <div className="table-responsive border rounded-3" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                <Table hover className="bound-issues-table mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }} className="text-center">Select</th>
                      <th>Volume</th>
                      <th>Issue #</th>
                      <th>Year</th>
                      <th>Month</th>
                      <th>Publication Date</th>
                      <th>Received Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedIssues.map((issue) => {
                      const isChecked = selectedIssues.some(i => getIssueKey(i) === getIssueKey(issue));
                      return (
                        <tr
                          key={getIssueKey(issue)}
                          className={isChecked ? 'table-primary' : ''}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleIssueSelection(issue)}
                        >
                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleIssueSelection(issue)}
                            />
                          </td>
                          <td className="fw-bold text-dark">Vol {issue.volNo ?? issue.volume ?? '-'}</td>
                          <td>
                            <span className="badge bg-light text-dark border">
                              #{issue.issueNumber}
                            </span>
                          </td>
                          <td>{issue.issueYear || issue.year || '-'}</td>
                          <td>
                            {issue.month || issue.issueMonth
                              ? new Date(2000, Number(issue.month || issue.issueMonth) - 1).toLocaleString('default', { month: 'short' })
                              : '-'}
                          </td>
                          <td>{issue.pubDate || '-'}</td>
                          <td>
                            {issue.receivedDate ? (
                              <span className="text-success fw-semibold">
                                <i className="bi bi-check-circle me-1"></i>
                                {issue.receivedDate}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <Badge bg={issue.issueStatus === 'Received' ? 'success' : 'warning'}>
                              {issue.issueStatus || 'Available'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <span className="small text-muted">
                  {selectedIssues.length > 0 ? (
                    <span>Ready to bind <strong>{selectedIssues.length}</strong> issue{selectedIssues.length === 1 ? '' : 's'}.</span>
                  ) : (
                    <span>Select issues from above to enable binding.</span>
                  )}
                </span>
                <div className="d-flex gap-2">
                  <Button variant="secondary" onClick={handleBoundClose} disabled={isSubmittingBound}>
                    Cancel
                  </Button>
                  <Button
                    className="btn-journal-primary"
                    onClick={handleBoundSubmit}
                    disabled={isSubmittingBound || selectedIssues.length === 0}
                  >
                    {isSubmittingBound ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" /> Creating Bound Volume...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-journal-check me-1"></i> Create Bound Volume ({selectedIssues.length} Issues)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="scroll-top-btn"
          title="Scroll to Top"
        >
          <i className="bi bi-arrow-up"></i>
        </button>
      )}
    </>
  );
}
